import { getPlanComparison, getPlans } from "@components/new-ui/PricingSection/constants";
import { useRouter } from "next/router";
import React, { useMemo, useState } from "react";
import { GoArrowDown, GoArrowUp, GoArrowUpRight } from "react-icons/go";
import { container_classname } from "../constants";
import Plan from "./Plan";
import PricingTable from "./PricingTable";
import PricingToogle from "./PricingToggleSection";
import Image from "next/image";
import { GBButtonWithIcon } from "../CTAButtons";

export type Period = "monthly" | "quarterly" | "yearly";

const indianPricingTabs = [
    {
        name: "monthly",
    },
    {
        name: "quarterly",
    },
    {
        name: "yearly",
    },
];
const usaPricingTabs = [
    {
        name: "quarterly",
    },
    {
        name: "yearly",
    },
];

interface PricingSectionProps {
    displayComparisonPlan?: boolean;
}

const PricingSection: React.FC<PricingSectionProps> = ({ displayComparisonPlan = true }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [radioValue, setRadioValue] = useState<Period>("yearly");
    const router = useRouter();

    const countryCode = router.query.country;

    const { isDollar, showMonthlyPlan, isarab } = useMemo(() => {
        const dateAsString = new Date().toString();
        if (countryCode) {
            const indiaRoute = ["in", "ind"].includes(countryCode as string);
            const uaeRoute = ["ae", "are", "kw", "kwt"].includes(countryCode as string);

            if (indiaRoute) {
                return { isDollar: false, showMonthlyPlan: true };
            } else if (uaeRoute) {
                return { isDollar: true, showMonthlyPlan: false, isarab: true };
            } else {
                return { isDollar: true, showMonthlyPlan: true };
            }
        } else {
            const indiaTimeZone = dateAsString.includes("India Standard Time") || new Date().toString().includes("IST");
            const isAbuDhabi = dateAsString.includes("Gulf Standard Time") || dateAsString.includes("GST");
            const isKuwait = dateAsString.includes("Arabian Standard Time") || dateAsString.includes("AST");
            if (indiaTimeZone) {
                return { isDollar: false, showMonthlyPlan: true };
            } else if (isAbuDhabi || isKuwait) {
                return { isDollar: true, showMonthlyPlan: false, isarab: true };
            } else {
                return { isDollar: true, showMonthlyPlan: true };
            }
        }
    }, [countryCode]);

    const pricingTabs = useMemo(() => {
        if (showMonthlyPlan) {
            return indianPricingTabs;
        } else {
            return usaPricingTabs;
        }
    }, [showMonthlyPlan, isDollar, isarab]);

    const handleRadioChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        return setRadioValue(e.target.value as Period);
    };

    const pricingPlanData = getPlans(isDollar, radioValue);
    const pricingHeaderData = getPlanComparison(isDollar, radioValue);

    return (
        <>
            <PricingToogle pricingTabs={pricingTabs} radioValue={radioValue} handleRadioChange={handleRadioChange} isDollar={isDollar} />

            <section className={`${container_classname} text-center pt-5 md:pb-10 flex justify-center md:items-center md:gap-2`}>
                <Image
                    src="/gift.png"
                    alt={"gift.png"}
                    width={24}
                    height={24}
                    className="md:h-6 md:w-6 h-6 w-6
                "
                />
                <p>
                    <b>Free, dedicated onboarding and premium support</b> with every annual subscription.
                </p>
            </section>

            <section className={`${container_classname} flex lg:flex-row gap-5 flex-col  pt-16 pb-16`} style={isDollar ? { minHeight: "1000px" } : { minHeight: "1170px" }}>
                {pricingPlanData.map((data) => (
                    <Plan pricingPlanData={data} isDollar={isDollar} key={data.name} />
                ))}
            </section>

            <section className="mx-auto max-w-7xl px-4 mb-20">
                <div className="border border-solid border-black rounded-md px-6 py-5 flex md:flex-row flex-col items-center justify-between" style={{ background: "#EDF2F7" }}>
                    <div className="flex flex-col gap-2 mb-5 md:mb-0 ">
                        <p className="font-semibold text-3xl md:text-left text-center">Looking for a solution tailored to your business?</p>
                        <p className="md:text-left text-center ">Our sales team is here to help with a personalized plan.</p>
                    </div>
                    <div>
                        <GBButtonWithIcon
                            rightIcon={GoArrowUpRight}
                            target="_blank"
                            href="https://tally.so/r/nP1Wq5"
                            rel="noreferrer"
                            variant="primary-dark"
                            style={{
                                width: "215px",
                                padding: "12px 24px",
                            }}
                        >
                            Contact sales
                        </GBButtonWithIcon>
                    </div>
                </div>
            </section>

            {displayComparisonPlan ? (
                <section className={`${container_classname} pb-11`}>
                    <div className={`${!isOpen ? "mb-0" : "mb-20"} flex justify-center`}>
                        <p className="flex items-center gap-2 text-lg bg-blue-600 text-white w-fit px-6 py-4 rounded-md" onClick={() => setIsOpen((prev) => !prev)}>
                            Compare features across plans
                            {isOpen ? <GoArrowUp fontSize="16px" /> : <GoArrowDown fontSize="16px" />}
                        </p>
                    </div>
                    {isOpen && <PricingTable {...pricingHeaderData} isDollar={isDollar} />}
                </section>
            ) : null}
        </>
    );
};

export default PricingSection;
